/**
 * @file constants.ts
 * @description Shared constants for the canvas components and hooks
 */

// ============================================================================
// BIN GROUP NODE DIMENSIONS
// ============================================================================

/** Default width for a bin group node (bins auto-grow to fit content, so new
 * ones start small; keep in sync with backend/src/board/index.ts). */
export const DEFAULT_BIN_WIDTH = 220

/** Default height for a bin group node */
export const DEFAULT_BIN_HEIGHT = 160
/** In-bin item spacing for auto-layout bins — tighter than the canvas node gap
 * so bins read as dense trays (PR #1009's original packing). */
export const BIN_ITEM_GAP = 8

/** Header height for bin group nodes */
export const BIN_HEADER_HEIGHT = 22
/**
 * Vertical room a bin's bottom label needs below its body (BOTTOM_STRIP_HEIGHT
 * + BOTTOM_LABEL_OFFSET in the engine's node chrome). Auto-layout parents add
 * this to a child bin's slot so the name is never covered by the next row.
 */
export const BIN_LABEL_BAND_HEIGHT = 26

/** Padding inside bin content area */
export const BIN_CONTENT_PADDING = 32

/** Collapsed bin height (header only) */
export const COLLAPSED_BIN_HEIGHT = BIN_HEADER_HEIGHT + 4

/** Minimum width for bin resize */
export const MIN_BIN_WIDTH = 200

/** Minimum height for bin resize */
export const MIN_BIN_HEIGHT = 150

/** Gap between bins at the same level */
export const BIN_GAP = 20

// ============================================================================
// NODE POSITIONING
// ============================================================================

/** Default starting X position for new nodes */
export const DEFAULT_START_X = 50

/** Default starting Y position for new nodes */
export const DEFAULT_START_Y = 50

/** Grid columns for auto-placing nodes without positions */
export const GRID_COLUMNS = 5

/** Grid columns for laying out items inside a bin */
export const BIN_GRID_COLUMNS = 3

// ============================================================================
// STACK LAYOUT
// ============================================================================

/** Height of collapsed stack header bar above the representative asset card. */
export const STACK_COLLAPSED_HEADER_HEIGHT = 18

/**
 * V3 expand/collapse Y offset — first member's Y minus the closed stack's
 * stored Y. Set to 0 in V3 so the closed thumbnail and the open first
 * member occupy the same canvas Y range. The closed frame's symmetric
 * vertical padding (`STACK_FRAME_TOP_PAD === STACK_FRAME_BOTTOM_PAD = 33`)
 * vertically centers the thumbnail in the border. The open backdrop uses
 * asymmetric `STACK_BACKDROP_PADDING_TOP/BOTTOM` to compensate for expanded
 * members' top bars, keeping the stack frame fixed across collapse/expand.
 *
 * Replaces `STACK_COLLAPSED_HEADER_HEIGHT` (18), which was tuned for the
 * React Flow canvas's 18-px header bar. There is no header bar now —
 * `closed_y` IS the thumbnail top — so no offset is needed.
 */
export const STACK_V3_EXPAND_Y_OFFSET = 0

/** Header row height for expanded stack backdrops. */
export const STACK_BACKDROP_HEADER_HEIGHT = 24

/**
 * Horizontal padding around members in expanded stack backdrops.
 *
 * V3 alignment: matches the closed-stack frame's lateral pad
 * (CAROUSEL_ARROW_SIZE 20 + CAROUSEL_ARROW_MARGIN 4 * 2 = 28) so the
 * open body border's left/right edges sit at the same canvas X as the
 * closed frame's, eliminating the horizontal jump on toggle.
 */
export const STACK_BACKDROP_PADDING_X = 28

/**
 * Top spacing between the expanded stack body border and first member row.
 *
 * V3 alignment: matches the closed-stack frame's top pad so expanding a
 * stack leaves the left, top, and bottom border positions visually fixed.
 */
export const STACK_BACKDROP_PADDING_TOP = 33

/**
 * Bottom padding around members in expanded stack backdrops.
 *
 * Expanded members include their 22px top bar in `calculateNodeDimensions()`;
 * collapsed stack cards are thumbnail-only. 11px bottom padding plus that
 * 22px member header equals the closed-stack frame's 33px bottom pad, so the
 * open and closed stack body borders share the same total height.
 */
export const STACK_BACKDROP_PADDING_BOTTOM = 11

// ============================================================================
// TEXT ANNOTATION NODE DIMENSIONS
// ============================================================================

/** Default width for a new text annotation */
export const DEFAULT_ANNOTATION_WIDTH = 200

/** Minimum width for annotation resize */
export const MIN_ANNOTATION_WIDTH = 80

/** Minimum height for annotation node */
export const MIN_ANNOTATION_HEIGHT = 24

// ============================================================================
// TOOLBAR DRAG AND DROP
// ============================================================================

/**
 * Offset applied when dropping an image/video node from the toolbar.
 * Centers the node under the cursor.
 * Uses standard node dimensions (200x134.5).
 */
export const NODE_DROP_OFFSET = { x: -100, y: -67.25 }
